// The apps' only way to the server (technical design §2): POST /rpc/<action> with the session
// cookie, the CSRF header and an idempotency key per command call. No server code at runtime.

export interface ClientError {
  code: string;
  message: string;
  detail?: unknown;
}

export class RpcError extends Error {
  readonly code: string;
  readonly detail: unknown;
  readonly status: number;
  constructor(status: number, e: ClientError) {
    super(e.message);
    this.name = 'RpcError';
    this.code = e.code;
    this.detail = e.detail;
    this.status = status;
  }
}

export type SignInMethod = 'password' | 'google' | 'microsoft' | 'email_code' | 'sms_code';
export type CodeTarget = ({ email: string } | { mobile: string }) & { tenant?: string };

/** The ways to give a second factor (decision 20). */
export type TwoFactorMethod = 'app' | 'sms' | 'recovery';
/** A first factor passed: the session, or the second factor still owed (decision 20). */
export type SignInResult =
  | { csrf: string; tenant: string; twoFactor?: undefined }
  /** company: whose sign-in this is (its display name), for the second step's head. */
  | { twoFactor: { methods: TwoFactorMethod[]; company?: string }; csrf?: undefined; tenant?: undefined };

export interface ClientOptions {
  baseUrl?: string;
  /** CSRF token from sign-in; required for every non-GET call with a cookie session. */
  csrf?: () => string | null;
  /** Bearer token instead of the cookie (scripts, tests). */
  token?: string;
  fetch?: typeof fetch;
  newKey?: () => string;
  /** Called when a call finds the session gone (401), before it throws: the app asks to sign in again. */
  onUnauthenticated?: () => void;
  /**
   * Called when a call is held because the person's required two-factor isn't set up past its
   * deadline (403 two_factor_setup_required), before it throws: the app opens the setup.
   */
  onTwoFactorRequired?: () => void;
  /**
   * Riding out a lost connection (QA-058): a call that can't reach the server (the network fails,
   * or a proxy answers 502, 503 or 504) is sent again with the same idempotency key, so a command
   * still lands exactly once, until `retryForMs` has passed; then it throws `offline`. `onChange`
   * hears every change between reachable and not. Off when left out.
   */
  connection?: {
    retryForMs: number;
    onChange?: (online: boolean) => void;
    wait?: (ms: number) => Promise<void>;
  };
}

/** The call never reached the server, so nothing it asked for was done. */
export const OFFLINE: ClientError = {
  code: 'offline',
  message: 'No connection, so this wasn’t saved. Try again when you’re back online.',
};
/**
 * QA-848: a try of a call given `answerWithinMs` wasn't answered in that time (the connection stalled:
 * sent, but nothing came back). It may have landed; sent again with the same idempotency key, it lands
 * once.
 */
export const NO_ANSWER: ClientError = {
  code: 'no_answer',
  message: 'The connection isn’t answering, so this may not have been saved. Try again: it’s only done once.',
};
const GATEWAY = new Set([502, 503, 504]);

/**
 * What a refused call says. Our server's answer has its own code and words; anything else came
 * from the way there (Fastify's own 413, a proxy's 429 or 502, a page that isn't JSON) and is said
 * in words a person can act on, never a bare "Couldn't save" (rs-office1).
 */
export function refusalOf(status: number, body: { error?: unknown } | null): ClientError {
  const e = body?.error as { code?: unknown; message?: unknown } | undefined;
  if (e && typeof e === 'object' && typeof e.code === 'string' && typeof e.message === 'string')
    return e as ClientError;
  if (status === 401) return { code: 'unauthenticated', message: 'Sign in again.' };
  if (status === 413) return { code: 'too_large', message: 'That’s too big to send in one go.' };
  if (status === 429) return { code: 'rate_limited', message: 'Too many tries; wait a moment.' };
  if (GATEWAY.has(status) || body === null)
    return { code: 'unreachable', message: 'Flowology couldn’t be reached; try again.' };
  return { code: 'internal', message: `Something went wrong on our side (${status}). Try again.` };
}
const RETRY_STEPS_MS = [1000, 2000, 3000];

export interface CallOptions {
  idempotencyKey?: string;
  /**
   * QA-848: the time a try has to be answered. One still waiting then (sent, nothing back: a stalled
   * connection) is let go and the call throws `no_answer` (it may have landed); without it a stalled
   * connection waits for ever. A try the network refuses (offline) is tried again as before.
   */
  answerWithinMs?: number;
}

export interface Client {
  call<T = unknown>(action: string, input?: unknown, opts?: CallOptions): Promise<T>;
  signIn(email: string, password: string, tenant?: string): Promise<SignInResult>;
  /** Is a second factor owed here (after Google, Microsoft or a link), and by which ways? */
  twoFactorPending(
    tenant?: string,
  ): Promise<{ pending: boolean; methods?: TwoFactorMethod[]; company?: string }>;
  /** Texts the second-factor code; the last digits of the mobile it went to. */
  twoFactorText(tenant?: string): Promise<{ sent: true; phoneTail: string }>;
  /** The second factor: the session, and how many recovery codes are left when one was used. */
  twoFactorVerify(v: {
    method: TwoFactorMethod;
    code: string;
    remember?: boolean;
    tenant?: string;
  }): Promise<{ csrf: string; tenant: string; recoveryCodesLeft?: number }>;
  /** The person's own account routes (/me/…): JSON in and out, with the session and CSRF. */
  me<T = unknown>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T>;
  /**
   * The company behind this host, before sign-in: its name, initials, colour and theme, and the design
   * the theme is for (tokenSet: riverstone or blueprint).
   */
  brand(tenant?: string): Promise<{
    name: string;
    initials: string;
    brandColor: string | null;
    css: string | null;
    tokenSet: string | null;
  }>;
  /** How this company's people may sign in (decision 19), and its name. */
  signInMethods(tenant?: string): Promise<{ company: string; methods: SignInMethod[] }>;
  /** Ask for a one-time code by email or text; the answer is the same whoever it is. */
  startCode(to: CodeTarget): Promise<{ sent: true; message: string }>;
  /** Sign in with the code. */
  verifyCode(to: CodeTarget & { code: string }): Promise<SignInResult>;
  session(): Promise<{
    tenant: string;
    /** The company's display name (its initials stone). */
    companyName?: string;
    principal: {
      membershipId?: string;
      staffId?: string | null;
      roles?: string[];
      /** What the person holds, by their roles and the owner's grants, sorted: what the screens may offer. */
      permissions?: string[];
    };
    /** The live change stream is this person's (they see every job): open it only then (QA-397). */
    liveUpdates?: boolean;
    /** 'mirror' while the company mirrors ServiceTitan: what ServiceTitan owns is changed there. */
    mode?: 'live' | 'mirror';
    /** A trial open on the company's data: everything changed is thrown away when it ends. */
    trial?: { state: 'starting' | 'on' | 'ending'; since: string | null };
    /** The browser's Google Maps key, when one is set: the web map loads with it (BL-018). */
    mapsKey?: string;
    /** Why there's no maps key (QA-756): the company has maps off, or the server has no key. */
    mapsOff?: 'company' | 'server';
    csrf: string;
  } | null>;
  signOut(): Promise<void>;
}

const randomKey = () =>
  globalThis.crypto?.randomUUID
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function createClient(opts: ClientOptions = {}): Client {
  const base = (opts.baseUrl ?? '').replace(/\/$/, '');
  const doFetch: typeof fetch = opts.fetch ?? ((input, init) => fetch(input, init));
  const headers = (extra: Record<string, string> = {}) => {
    const h: Record<string, string> = { 'content-type': 'application/json', ...extra };
    if (opts.token) h.authorization = `Bearer ${opts.token}`;
    const csrf = opts.csrf?.();
    if (csrf) h['x-flow-csrf'] = csrf;
    return h;
  };
  const conn = opts.connection;
  let online = true;
  const seen = (ok: boolean) => {
    if (ok === online) return;
    online = ok;
    conn?.onChange?.(ok);
  };
  const wait = conn?.wait ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  /** One fetch, again after a lost connection while `connection` allows (same request, same key). */
  const send = async (url: string, init: RequestInit, within?: number): Promise<Response> => {
    const started = Date.now();
    for (let attempt = 0; ; attempt++) {
      let res: Response | null;
      // a try that gets no answer in its time is let go (QA-848); one the network refuses at once
      // (offline) is tried again as before, for the connection's retryForMs
      const stop = within === undefined ? null : new AbortController();
      let late = false;
      const timer = stop
        ? setTimeout(() => {
            late = true;
            stop.abort();
          }, within!)
        : null;
      try {
        res = await doFetch(url, stop ? { ...init, signal: stop.signal } : init);
      } catch (e) {
        if (late) throw new RpcError(0, NO_ANSWER);
        if (!conn) throw e;
        res = null; // the network failed: nothing reached the server
      } finally {
        if (timer) clearTimeout(timer);
      }
      if (!conn) return res!;
      if (res && !GATEWAY.has(res.status)) {
        seen(true);
        return res;
      }
      seen(false);
      const step = RETRY_STEPS_MS[Math.min(attempt, RETRY_STEPS_MS.length - 1)]!;
      if (Date.now() - started + step > conn.retryForMs) throw new RpcError(0, OFFLINE);
      await wait(step);
    }
  };
  const held = (e: unknown) =>
    e instanceof RpcError &&
    e.status === 403 &&
    (e.detail as { reason?: string } | undefined)?.reason === 'two_factor_setup_required';
  // Parses an rpc or /me answer; a hold for two-factor setup is reported once it is read.
  const answer = async (res: Response) => {
    try {
      return await parse(res);
    } catch (e) {
      if (held(e)) opts.onTwoFactorRequired?.();
      throw e;
    }
  };
  const parse = async (res: Response) => {
    const body = (await res.json().catch(() => null)) as {
      ok?: boolean;
      result?: unknown;
      error?: unknown;
    } | null;
    if (!res.ok || !body?.ok) throw new RpcError(res.status, refusalOf(res.status, body));
    return body.result;
  };
  return {
    async call<T>(action: string, input: unknown = {}, o: CallOptions = {}) {
      const res = await send(
        `${base}/rpc/${action}`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: headers({ 'idempotency-key': o.idempotencyKey ?? (opts.newKey ?? randomKey)() }),
          body: JSON.stringify(input),
        },
        o.answerWithinMs,
      );
      if (res.status === 401) opts.onUnauthenticated?.(); // not for sign-in itself: only a call
      return (await answer(res)) as T;
    },
    async signIn(email, password, tenant) {
      const res = await doFetch(`${base}/auth/login`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers(),
        body: JSON.stringify({ email, password, ...(tenant ? { tenant } : {}) }),
      });
      return (await parse(res)) as SignInResult;
    },
    async twoFactorPending(tenant) {
      const q = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
      const res = await doFetch(`${base}/auth/2fa${q}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: headers(),
      });
      return (await parse(res)) as { pending: boolean; methods?: TwoFactorMethod[]; company?: string };
    },
    async twoFactorText(tenant) {
      const res = await doFetch(`${base}/auth/2fa/sms`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers(),
        body: JSON.stringify(tenant ? { tenant } : {}),
      });
      return (await parse(res)) as { sent: true; phoneTail: string };
    },
    async twoFactorVerify(v) {
      const res = await doFetch(`${base}/auth/2fa/verify`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers(),
        body: JSON.stringify(v),
      });
      return (await parse(res)) as { csrf: string; tenant: string; recoveryCodesLeft?: number };
    },
    async me<T>(method: 'GET' | 'POST', path: string, body?: unknown) {
      const res = await send(`${base}${path}`, {
        method,
        credentials: 'same-origin',
        headers: headers(),
        ...(method === 'POST' ? { body: JSON.stringify(body ?? {}) } : {}),
      });
      if (res.status === 401) opts.onUnauthenticated?.();
      return (await answer(res)) as T;
    },
    async brand(tenant) {
      const q = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
      const res = await doFetch(`${base}/brand${q}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: headers(),
      });
      return (await parse(res)) as Awaited<ReturnType<Client['brand']>>;
    },
    async signInMethods(tenant) {
      const q = tenant ? `?tenant=${encodeURIComponent(tenant)}` : '';
      const res = await doFetch(`${base}/auth/methods${q}`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: headers(),
      });
      return (await parse(res)) as { company: string; methods: SignInMethod[] };
    },
    async startCode(to) {
      const res = await doFetch(`${base}/auth/code/start`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers(),
        body: JSON.stringify(to),
      });
      return (await parse(res)) as { sent: true; message: string };
    },
    async verifyCode(to) {
      const res = await doFetch(`${base}/auth/code/verify`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers(),
        body: JSON.stringify(to),
      });
      return (await parse(res)) as SignInResult;
    },
    async session() {
      const res = await doFetch(`${base}/auth/session?optional=1`, {
        method: 'GET',
        credentials: 'same-origin',
        headers: headers(),
      });
      if (res.status === 401) return null;
      return (await parse(res)) as Awaited<ReturnType<Client['session']>>;
    },
    async signOut() {
      // A JSON body, even empty: the api refuses a JSON content type with no body, and the session
      // would outlive "Sign out".
      await doFetch(`${base}/auth/logout`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: headers(),
        body: '{}',
      });
    },
  };
}
